import React, { useState } from 'react'
import { Modal } from '@meiuca/ui-components'
import { Button, ButtonVariant, ButtonStyle } from '@meiuca/ui-components'
import { Input } from '@meiuca/ui-components'
import { PlaceAutocomplete } from './PlaceAutocomplete'
import { MapPin, Building2 } from 'lucide-react'
import type { GameLocation } from '../types/game'
import { arenaService } from '../services/arenaService'

interface SuggestArenaModalProps {
  open: boolean
  onClose: () => void
  onSuccess?: (message: string) => void
  onError?: (message: string) => void
}

interface ArenaFormData {
  location: GameLocation | null
  manualName: string
  manualCity: string
  manualState: string
}

export function SuggestArenaModal({ open, onClose, onSuccess, onError }: SuggestArenaModalProps) {
  const [formData, setFormData] = useState<ArenaFormData>({
    location: null,
    manualName: '',
    manualCity: '',
    manualState: '',
  })

  const [useManualForm, setUseManualForm] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const handleInputChange = (field: keyof ArenaFormData, value: any) => {
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    if (useManualForm) {
      if (!formData.manualName.trim()) {
        errors.manualName = 'Nome da arena é obrigatório'
      }
      if (!formData.manualCity.trim()) {
        errors.manualCity = 'Cidade é obrigatória'
      }
      if (!formData.manualState.trim()) {
        errors.manualState = 'Estado é obrigatório'
      }
    } else {
      if (!formData.location) {
        errors.location = 'Selecione um local da lista ou use o formulário manual'
      }
    }

    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      const suggestionData = useManualForm
        ? {
            type: 'manual' as const,
            name: formData.manualName,
            city: formData.manualCity,
            state: formData.manualState,
          }
        : {
            type: 'google_place' as const,
            name: formData.location!.name,
            address: formData.location!.address,
            googlePlaceId: formData.location!.googlePlaceID,
            coordinate: formData.location!.coordinate,
          }

      await arenaService.suggestArena(suggestionData)

      onSuccess?.('Obrigado pela sugestão! Vamos analisar e adicionar a arena em breve.')
      handleClose()
    } catch (error) {
      console.error('Error submitting arena suggestion:', error)
      onError?.('Erro ao enviar sugestão. Por favor, tente novamente.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setFormData({
      location: null,
      manualName: '',
      manualCity: '',
      manualState: '',
    })
    setUseManualForm(false)
    setFieldErrors({})
    setIsSubmitting(false)
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Indicar uma arena"
      size="lg"
      closeOnOverlayClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <div className="p-6">
        <div className="mb-6">
          <p className="text-gray-600 text-sm">
            Ajude a comunidade a crescer! Indique arenas, quadras ou estádios na sua região.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {!useManualForm ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Busque o local
                </label>
                <PlaceAutocomplete
                  value={formData.location || {
                    address: '',
                    name: '',
                    coordinate: { latitude: 0, longitude: 0 }
                  }}
                  onChange={(location) => handleInputChange('location', location)}
                  placeholder="Digite o nome da arena, quadra ou estádio..."
                  establishmentsOnly={true}
                />
                {fieldErrors.location && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.location}</p>
                )}
              </div>

              <div className="pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setUseManualForm(true)}
                  className="text-sm text-brand-primary hover:text-brand-primary/80 font-medium flex items-center gap-2"
                >
                  <Building2 className="w-4 h-4" />
                  Não encontrou? Preencher manualmente
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-700">Informações da Arena</h3>
                <button
                  type="button"
                  onClick={() => setUseManualForm(false)}
                  className="text-sm text-brand-primary hover:text-brand-primary/80 font-medium flex items-center gap-1"
                >
                  <MapPin className="w-4 h-4" />
                  Buscar no mapa
                </button>
              </div>

              <Input
                type="text"
                label="Nome da arena"
                placeholder="Ex: Arena Futebol Clube"
                value={formData.manualName}
                onChange={(e) => handleInputChange('manualName', e.target.value)}
                error={!!fieldErrors.manualName}
                errorMessage={fieldErrors.manualName}
                fullWidth
                required
              />

              <Input
                type="text"
                label="Cidade"
                placeholder="Ex: São Paulo"
                value={formData.manualCity}
                onChange={(e) => handleInputChange('manualCity', e.target.value)}
                error={!!fieldErrors.manualCity}
                errorMessage={fieldErrors.manualCity}
                fullWidth
                required
              />

              <Input
                type="text"
                label="Estado"
                placeholder="Ex: SP"
                value={formData.manualState}
                onChange={(e) => handleInputChange('manualState', e.target.value)}
                error={!!fieldErrors.manualState}
                errorMessage={fieldErrors.manualState}
                fullWidth
                required
                maxLength={2}
              />
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              buttonStyle={ButtonStyle.BORDERED}
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant={ButtonVariant.PRIMARY}
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? 'Enviando...' : 'Enviar sugestão'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  )
}
